import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import firebase from 'firebase/app';
import * as Sentry from '@sentry/browser';
import format from 'string-template';
import IconButton from '@mui/material/IconButton';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import useComponentMounted from '../../../hooks/useComponentMounted';
import { UserExerciseOverrideAction } from '../../../Model/UserExerciseOverrides';
import ConfirmDialog from '../ConfirmDialog';
import DialogModal from '../../../components/DialogModal';
import LoadingOverlay from '../LoadingOverlay';
import ExercisesTable from '../../pages/WorkoutsManager/Exercises/ExercisesTable';
import useToast from '../../hooks/useToast';
import remote from '../../../utils/remote';
import { ButtonRow } from './styles';
import texts from './texts.json';

const OverrideActions = ({
  userId,
  originalExerciseId,
  originalName,
  currentReplacementName,
}) => {
  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingReplacement, setPendingReplacement] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const sendAction = useCallback(async (action, extraBody, successText, errorText, closeDialog) => {
    setIsLoading(true);
    try {
      const idToken = await firebase.auth().currentUser.getIdToken();
      const response = await remote(idToken, 'updateUserExerciseOverride', {
        userId,
        originalExerciseId,
        action,
        ...extraBody,
      });
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.error || `Update exercise override failed with status ${response.status}`);
      }
      if (isComponentMountedRef.current) {
        showToast(successText);
        setIsLoading(false);
        closeDialog();
      }
    } catch (error) {
      Sentry.captureException(error);
      if (isComponentMountedRef.current) {
        showToast(errorText, { error: true });
        setIsLoading(false);
        closeDialog();
      }
    }
  }, [userId, originalExerciseId, showToast, isComponentMountedRef]);

  const handleRemoveConfirm = useCallback(() => sendAction(
    UserExerciseOverrideAction.REMOVE,
    {},
    texts.removeSuccess,
    texts.removeError,
    () => setConfirmRemoveOpen(false),
  ), [sendAction]);

  const handlePickerSelect = useCallback((newExercise) => {
    setPickerOpen(false);
    if (!newExercise) return;
    setPendingReplacement(newExercise);
  }, []);

  const handleReplaceConfirm = useCallback(() => {
    if (!pendingReplacement) return;
    sendAction(
      UserExerciseOverrideAction.REPLACE,
      { newReplacementExerciseId: pendingReplacement.id },
      texts.replaceSuccess,
      texts.replaceError,
      () => setPendingReplacement(null),
    );
  }, [sendAction, pendingReplacement]);

  return (
    <>
      <ButtonRow>
        <IconButton
          size="small"
          aria-label={texts.replaceAria}
          onClick={() => setPickerOpen(true)}
        >
          <SwapHorizIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          aria-label={texts.removeAria}
          onClick={() => setConfirmRemoveOpen(true)}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </ButtonRow>

      <ConfirmDialog
        isOpen={confirmRemoveOpen}
        onConfirm={handleRemoveConfirm}
        onCancel={() => setConfirmRemoveOpen(false)}
        dialogTexts={{
          title: texts.removeConfirm.title,
          content: format(texts.removeConfirm.description, {
            original: originalName,
            replacement: currentReplacementName,
          }),
          confirm: texts.removeConfirm.confirm,
          cancel: texts.removeConfirm.cancel,
        }}
      />

      <DialogModal open={pickerOpen} onClose={() => setPickerOpen(false)} fullWidth maxWidth="md">
        <ExercisesTable
          isExerciseChangeView
          handleActionClick={handlePickerSelect}
        />
      </DialogModal>

      <ConfirmDialog
        isOpen={!!pendingReplacement}
        onConfirm={handleReplaceConfirm}
        onCancel={() => setPendingReplacement(null)}
        dialogTexts={{
          title: texts.replaceConfirm.title,
          content: format(texts.replaceConfirm.description, {
            original: originalName,
            currentReplacement: currentReplacementName,
            newReplacement: pendingReplacement?.name || '',
          }),
          confirm: texts.replaceConfirm.confirm,
          cancel: texts.replaceConfirm.cancel,
        }}
      />

      <LoadingOverlay isLoading={isLoading} />
    </>
  );
};

OverrideActions.propTypes = {
  userId: PropTypes.string.isRequired,
  originalExerciseId: PropTypes.string.isRequired,
  originalName: PropTypes.string.isRequired,
  currentReplacementName: PropTypes.string.isRequired,
};

export default OverrideActions;
